import { useRef } from 'react'
import { Fa } from '@eltoro-ui/components'
import ReactSelect from 'react-select/creatable'
import {
  ClearIndicatorProps,
  components,
  DropdownIndicatorProps,
  InputProps,
  MultiValueRemoveProps,
} from 'react-select'
import classNames from 'classnames'
import { AUDIENCE_TAG_LIMIT } from 'Helpers'
import { reactSelectStyles, reactSelectMultiStyles } from 'Components'

const getHighlightedText = (text: string, highlight: string) => {
  // Split on highlight term and include term into parts, ignore case
  const parts = text.split(new RegExp(`(${highlight})`, 'gi'))
  return (
    <span>
      {parts.map((part, i) => (
        <span
          key={i}
          style={
            part.toLowerCase() === highlight.toLowerCase()
              ? { fontWeight: 'bold', backgroundColor: 'var(--tint-khaki-100)' }
              : {}
          }
        >
          {part}
        </span>
      ))}
    </span>
  )
}

export const CustomAudienceTagDropdown = ({
  value,
  onChange,
  disabled,
  isMulti = false,
}: {
  value: string | string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  isMulti?: boolean
}) => {
  const ref = useRef(null)

  return (
    <div className="CustomAudienceTagDropdown relative w-full">
      <ReactSelect
        ref={ref}
        isMulti={isMulti}
        components={{
          DropdownIndicator: (props: DropdownIndicatorProps) => (
            <components.DropdownIndicator {...props}>
              <Fa icon="chevron-down" size={1} />
            </components.DropdownIndicator>
          ),
          ClearIndicator: (props: ClearIndicatorProps) => (
            <components.ClearIndicator {...props}>
              <Fa icon="xmark" size={1} />
            </components.ClearIndicator>
          ),
          Option: (props: any) => {
            return (
              <>
                <components.Option {...props}>
                  <div className="flex justify-between w-full">
                    {props.data?.value
                      ? getHighlightedText(
                          props.children,
                          props.selectProps?.inputValue,
                        )
                      : props.children}
                    {!isMulti && value === props.data.value && (
                      <Fa icon="check" size={1} />
                    )}
                    {isMulti &&
                      Array.isArray(value) &&
                      value.includes(props.data.value) && (
                        <Fa icon="check" size={1} />
                      )}
                  </div>
                </components.Option>
                {props.data?.__isNew__ && props.data?.value?.length && (
                  <div
                    className={classNames(
                      'flex justify-between pt-1 text-right text-xs',
                      {
                        'text-warning-500':
                          props.data.value.length >= AUDIENCE_TAG_LIMIT,
                      },
                    )}
                  >
                    <p>Limit {AUDIENCE_TAG_LIMIT} characters</p>
                    <p className="font-semibold">
                      Remaining: {AUDIENCE_TAG_LIMIT - props.data.value.length}
                    </p>
                  </div>
                )}
              </>
            )
          },
          MultiValueRemove: (props: MultiValueRemoveProps) => (
            <components.MultiValueRemove {...props}>
              <span
                className="ml-2 cursor-pointer font-bold"
                aria-label={`Remove ${
                  (props?.data as {
                    label: string
                    value: string
                  })?.label
                }`}
              >
                ✕
              </span>
            </components.MultiValueRemove>
          ),
          Input: (props: InputProps) => {
            return (
              <components.Input {...props} maxLength={AUDIENCE_TAG_LIMIT + 1} />
            )
          },
        }}
        styles={isMulti ? reactSelectMultiStyles : reactSelectStyles}
        className="CustomAudienceTagSelect"
        classNamePrefix="CustomAudienceTagSelect"
        value={
          !value
            ? null
            : isMulti && Array.isArray(value)
            ? value.map((tag) => ({ value: tag, label: tag }))
            : !isMulti
            ? { value: value as string, label: value as string }
            : null
        }
        onChange={(newValue: any) => {
          if (isMulti && Array.isArray(newValue)) {
            const values = newValue.map((item) =>
              item.value.slice(0, AUDIENCE_TAG_LIMIT),
            )
            onChange(values)
          } else {
            onChange([newValue?.value?.slice(0, AUDIENCE_TAG_LIMIT) || ''])
          }
        }}
        isClearable
        isSearchable
        menuPortalTarget={document.body}
        options={[
          'IP+',
          'L2',
          'Aristotle',
          'KnowWho',
          'Intent-to-Home',
          'Venue Replay+',
          'CoreLogic',
          'Garage Data',
          'Likely to Buy Home',
        ].map((tag) => ({
          label: tag,
          value: tag,
        }))}
        placeholder="Select / Add Audience Tag"
        isDisabled={disabled}
        // preventing the border to stay in focus style
        onMenuClose={() => (ref.current as any)?.blur()}
      />
    </div>
  )
}
